import { inject, injectable, optional } from 'inversify';
import { BehaviorSubject, Subject, Subscription, firstValueFrom } from 'rxjs';
import { filter } from 'rxjs/operators';

import {
    BulkZipFileProof,
    BulkZipJobView,
    BulkZipProgressEvent,
    BulkZipSubmitResponse,
    BulkZipTerminalEvent,
} from '../types/bulk-zip';
import { WebsocketResponse } from '../types/types';
import { ApiConfig } from './api-config';
import { FileManagerService } from './file-manager.service';
import { LightHttp } from './light-http.service';
import { WebSocketService } from './websocket.service';

/**
 * Discriminated union of events `BulkZipService` publishes on
 * `events$`. Components subscribe to surface toasts, navigate
 * to the Downloads Center, or refresh local job state.
 */
export type BulkZipServiceEvent =
    | { kind: 'CREATED'; job: BulkZipJobView }
    | { kind: 'EXISTING'; job: BulkZipJobView }
    | { kind: 'COVERED'; coverage: BulkZipSubmitResponse['coverage'] }
    | { kind: 'PROGRESS'; ev: BulkZipProgressEvent }
    | { kind: 'TERMINAL'; ev: BulkZipTerminalEvent };

/**
 * Backend `ProcessType` wire value for bulk-zip events.
 *
 * Matches the kotlin enum value added in
 * `springboot-shared-library/.../workflows/ProcessType.kt`
 * (`BULK_ZIP_DOWNLOAD("bulk-zip-download")`). The convention
 * across `ProcessType` is lowercase-plus-dashes, so this is
 * the value that arrives on the wire.
 */
const BULK_ZIP_PROCESS_TYPE = 'bulk-zip-download';

/**
 * Frontend service for the Bulk ZIP download feature.
 *
 * Responsibilities:
 *  - Submit bulk-zip jobs (`submit`).
 *  - List the user's bulk-zip jobs (`list`).
 *  - Delete a bulk-zip job (`delete`).
 *  - Subscribe to WebSocket progress/terminal events for the
 *    user's active jobs and project them onto typed
 *    `BulkZipServiceEvent`s.
 *  - Maintain a cached `BehaviorSubject<BulkZipJobView[]>` so
 *    components (Downloads Center, completion banner) can
 *    render the latest state without re-fetching.
 *
 * The WebSocket layer (`WebSocketService.getWebsocketMessageObserver`)
 * delivers a multiplexed stream — one observable for every
 * notification the user receives. We filter on
 * `processType === BULK_ZIP_PROCESS_TYPE` AND `uuid` being
 * one of our cached jobIds, so unrelated convert-one-to-one
 * messages don't churn `jobs$`.
 */
@injectable()
export class BulkZipService {
    public readonly events$ = new Subject<BulkZipServiceEvent>();
    public readonly jobs$ = new BehaviorSubject<BulkZipJobView[]>([]);

    /**
     * Fires when something external (e.g. the header pill) asks
     * the Downloads Center to open. The center subscribes to
     * this and flips `open: true`. Distinct from `events$` so
     * that "open without context" doesn't have to be modeled as
     * a fake CREATED/EXISTING/COVERED event.
     */
    public readonly openRequested$ = new Subject<void>();

    private wsSubscription?: Subscription;

    /**
     * fileId → owning FileUpload id, remembered from every proof this browser has sent, so a retry
     * of a job whose cards were since cleared can still prove the files it was built from.
     */
    private readonly knownProofs = new Map<string, string>();

    constructor(
        @inject(LightHttp) private http: LightHttp,
        @inject(WebSocketService) private ws: WebSocketService,
        @inject(ApiConfig) private apiConfig: ApiConfig,
        @inject(FileManagerService) @optional() private fileManager?: Pick<FileManagerService, 'getFileUploads'>,
    ) {
        this.wsSubscription = this.ws.getWebsocketMessageObserver()
            .pipe(filter((msg) => this.isBulkZipMessage(msg)))
            .subscribe((msg) => this.handleWsMessage(msg));
    }

    /**
     * Submit a bulk-zip request for the supplied fileIds. The
     * server may respond with one of three statuses:
     *  - JOB_CREATED: a brand-new job was queued.
     *  - JOB_EXISTING: an active job already covers the same
     *    fileIds (de-duped server-side; we surface the existing
     *    job view).
     *  - ALL_FILES_COVERED: every supplied fileId is already
     *    bundled in a recent completed job; no new job needed.
     *
     * fileIds are sorted client-side before POSTing so the
     * server's de-dup signature lookup is deterministic
     * regardless of UI ordering.
     */
    async submit(rawFileIds: string[]): Promise<BulkZipSubmitResponse> {
        const fileIds = [...rawFileIds].sort();
        // Review 7 X1-01: the server no longer trusts the IP; every fileId must come with its
        // owner proof (the FileUpload id this browser converted it under). WC-10: a file this
        // browser cannot prove used to be sent anyway, and the server 404'd the WHOLE batch; it is
        // now left out and named (`skipped`), so the provable ones still zip. Without a file
        // manager to prove anything from (a harness) the list goes as it is.
        const files = this.proofsFor(fileIds);
        const canProve = !!this.fileManager;
        const proven = new Set(files.map((f) => f.fileId));
        const sendIds = canProve ? fileIds.filter((id) => proven.has(id)) : fileIds;
        const skipped = canProve ? fileIds.filter((id) => !proven.has(id)) : [];
        if (sendIds.length === 0) {
            throw Object.assign(new Error('bulk-zip: none of these files can be proven to be this browser\'s'), { status: 404, unprovable: skipped });
        }
        const answer = await firstValueFrom(
            this.http.post<BulkZipSubmitResponse>(
                this.apiConfig.getBulkZipBaseEndpoint(),
                { fileIds: sendIds, files },
            ),
        );
        const resp: BulkZipSubmitResponse = skipped.length > 0 ? { ...answer, skipped } : answer;
        if (resp.status === 'ALL_FILES_COVERED') {
            this.events$.next({ kind: 'COVERED', coverage: resp.coverage });
        } else if (resp.job) {
            this.upsertJob(resp.job);
            this.events$.next({
                kind: resp.status === 'JOB_CREATED' ? 'CREATED' : 'EXISTING',
                job: resp.job,
            });
        }
        return resp;
    }

    /** List the current user's bulk-zip jobs. */
    async list(): Promise<BulkZipJobView[]> {
        const jobs = await firstValueFrom(
            this.http.get<BulkZipJobView[]>(this.apiConfig.getBulkZipBaseEndpoint()),
        );
        this.jobs$.next(jobs ?? []);
        return jobs ?? [];
    }

    /** Delete a bulk-zip job (and its artifact) by jobId. */
    async delete(jobId: string): Promise<void> {
        await firstValueFrom(
            this.http.delete<void>(`${this.apiConfig.getBulkZipBaseEndpoint()}/${jobId}`),
        );
        this.jobs$.next(this.jobs$.value.filter((j) => j.jobId !== jobId));
    }

    /**
     * Build the public download URL for a completed job. Does
     * NOT trigger a network call — the caller embeds this URL
     * in an `<a download>` or fetch call.
     */
    downloadUrl(jobId: string): string {
        const base = `${this.apiConfig.getBulkZipBaseEndpoint()}/${jobId}/download`;
        // Review 7 X1-01: a navigation carries no Authorization header, so ownership rides as the
        // owner-bound token from this browser's own job view. Without it the server answers 404.
        const dt = this.jobs$.value.find((j) => j.jobId === jobId)?.downloadToken;
        return dt ? `${base}?dt=${encodeURIComponent(dt)}` : base;
    }

    /** The owner proof for each fileId this browser can prove — see [submit]. */
    private proofsFor(fileIds: string[]): BulkZipFileProof[] {
        try {
            for (const fu of this.fileManager?.getFileUploads() ?? []) {
                const outs = (fu?.result?.outputFiles as string[] | undefined) ?? [];
                for (const out of outs) {
                    if (out && fu?.id) this.knownProofs.set(out, fu.id);
                }
            }
        } catch {
            // The proof map is best-effort; the server is the gate.
        }
        return fileIds.flatMap((fileId) => {
            const uuid = this.knownProofs.get(fileId);
            return uuid ? [{ fileId, uuid }] : [];
        });
    }

    /** Current snapshot of cached jobs (synchronous). */
    getCachedJobs(): BulkZipJobView[] {
        return this.jobs$.value;
    }

    /**
     * Refresh the cached job list from the server and ask the
     * Downloads Center to surface itself. Used by the header
     * pill and any "show my bulk downloads" entry points.
     */
    openCenter(): void {
        // Fire-and-forget — center renders eagerly from the
        // current cache and updates when `list()` resolves.
        void this.list();
        this.openRequested$.next();
    }

    /**
     * Tear down the long-lived WS subscription. Mostly for
     * tests / hot-reload — singletons live for the page
     * lifetime in production.
     */
    public dispose(): void {
        if (this.wsSubscription) {
            this.wsSubscription.unsubscribe();
            this.wsSubscription = undefined;
        }
    }

    private isBulkZipMessage(msg: WebsocketResponse): boolean {
        // Compare against the literal string; cast to unknown
        // so TS doesn't complain about WebsocketProcessType not
        // including the bulk-zip variant yet.
        if ((msg.processType as unknown as string) !== BULK_ZIP_PROCESS_TYPE) {
            return false;
        }
        return this.jobs$.value.some((j) => j.jobId === msg.uuid);
    }

    private handleWsMessage(msg: WebsocketResponse): void {
        const payload = msg.data ? safeJsonParse(msg.data) : {};
        const stage = String(msg.processStage);

        if (stage === 'BUILDING') {
            const ev: BulkZipProgressEvent = {
                type: 'BULK_ZIP_PROGRESS',
                jobId: msg.uuid,
                bytesFlushed: payload.bytesFlushed ?? 0,
                totalBytes: payload.totalBytes ?? 0,
                filesCompleted: payload.filesCompleted ?? 0,
                totalFiles: payload.totalFiles ?? 0,
                currentFile: payload.currentFile ?? null,
                ts: payload.ts ?? new Date().toISOString(),
            };
            this.applyProgress(ev);
            this.events$.next({ kind: 'PROGRESS', ev });
        } else if (stage === 'COMPLETED') {
            const failed = String(msg.processStageSubState).toUpperCase() === 'FAILED';
            const ev: BulkZipTerminalEvent = {
                type: failed ? 'BULK_ZIP_FAILED' : 'BULK_ZIP_COMPLETED',
                jobId: msg.uuid,
                errorCode: payload.errorCode,
                ts: payload.ts ?? new Date().toISOString(),
            };
            this.applyTerminal(ev);
            this.events$.next({ kind: 'TERMINAL', ev });
        }
        // BULK_ZIP_DELETED handling deferred — backend defines
        // the wire path for delete-broadcasts (e.g. multi-tab
        // delete sync) once phase 3 lands.
    }

    private upsertJob(job: BulkZipJobView): void {
        const next = this.jobs$.value.filter((j) => j.jobId !== job.jobId);
        next.unshift(job);
        this.jobs$.next(next);
    }

    private applyProgress(ev: BulkZipProgressEvent): void {
        this.jobs$.next(
            this.jobs$.value.map((j) =>
                j.jobId === ev.jobId
                    ? {
                          ...j,
                          bytesFlushed: ev.bytesFlushed,
                          totalBytes: ev.totalBytes || j.totalBytes,
                          filesCompleted: ev.filesCompleted,
                          totalFiles: ev.totalFiles || j.totalFiles,
                          currentFile: ev.currentFile,
                          processStage: 'BUILDING',
                          processStageSubState: 'IN_PROGRESS',
                      }
                    : j,
            ),
        );
    }

    private applyTerminal(ev: BulkZipTerminalEvent): void {
        this.jobs$.next(
            this.jobs$.value.map((j) =>
                j.jobId === ev.jobId
                    ? {
                          ...j,
                          processStage: 'COMPLETED',
                          processStageSubState: ev.type === 'BULK_ZIP_FAILED' ? 'FAILED' : 'COMPLETED',
                          failed: ev.type === 'BULK_ZIP_FAILED',
                      }
                    : j,
            ),
        );
    }
}

function safeJsonParse(s: string): any {
    try {
        return JSON.parse(s);
    } catch {
        return {};
    }
}
